import React, { useEffect, useState } from "react";
import "./SubHeader.scss";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { Menu, MenuItem, Button, useTheme } from "@mui/material";
import { ExpandMore, KeyboardArrowRight } from "@mui/icons-material";
import { R_DASHBOARD, R_PAYROLL_REPORTS } from "../../../utils/permissionReferenceConfig";
import DashboardIcon from "../../Assets/IconComponents/Dashboard";
import { useFeatureFlagEnabled } from "posthog-js/react";
import ScheduleReportModal from "../ScheduleReportModal";
import { useSelector } from "react-redux";
import { routerSelector } from "../../store/routerSlice";

const formatKey = key => {
  return key.split(" ").join("-").toLowerCase();
};

const ReportItemSubMenuItem = ({ menu }) => {
  const [open, setOpen] = useState(false);
  const { filters } = menu;
  const downloadOnlyEnabled = menu.enableAsyncDownload === true;
  const enableMultiProjectPartnerFilters = filters?.multiProjectPartnerFilter === true;
  return (
    <>
      <MenuItem onClick={() => setOpen(true)} key={formatKey(menu.title)} sx={{ width: "100%" }}>
        {menu.title}
      </MenuItem>
      <ScheduleReportModal
        isModalOpen={open}
        setIsModalOpen={setOpen}
        reportId={menu.reportId}
        showScheduleSection={false}
        reportLink={`/${menu.link}`}
        enableMultiProjectPartnerFilters={enableMultiProjectPartnerFilters}
        downloadOnlyEnabled={downloadOnlyEnabled}
        filters={filters}
        buttonText={downloadOnlyEnabled ? "Download" : "Open"}
      />
    </>
  );
};

const MuiSubMenuWrapper = ({ menuItemLabel, subMenus }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <div
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        style={{ color: theme.palette.subheaderv2.menuLabel, display: "flex", justifyContent: "space-between", padding: "10px", width: "100%" }}
      >
        <div>{menuItemLabel}</div>
        <KeyboardArrowRight fontSize="small" sx={{ display: "inline-block", verticalAlign: "middle", marginLeft: "10px" }} />
      </div>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button"
        }}
      >
        {subMenus.map(menu => {
          return (
            <div>
              {menu.reportItem ? (
                <ReportItemSubMenuItem menu={menu} />
              ) : (
                <Link to={`${menu.link}`}>
                  <MenuItem key={formatKey(menu.title)} sx={{ width: "100%" }}>
                    {menu.title}
                  </MenuItem>
                </Link>
              )}
            </div>
          );
        })}
      </Menu>
    </div>
  );
};

const MuiMenuWrapper = ({ dropdownTitle, Icon, hasArrow, href, subMenus }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const navigator = useNavigate();
  const theme = useTheme();
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const isCprEnabled = useFeatureFlagEnabled("is_v2_cpr_format_enabled");
  const isFringeBenefitEnabled = useFeatureFlagEnabled("is_fringe_accounts_selection_enabled");

  return (
    <>
      <Button
        className="tab"
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        href={href}
      >
        <div className="tab_main">
          <Icon color={theme.palette.subheaderv2.icons} />
          <span className="menu-title">{dropdownTitle}</span>
          {hasArrow && <ExpandMore htmlColor={theme.palette.subheaderv2.icons} />}
        </div>
      </Button>
      {hasArrow && (
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button"
          }}
        >
          {subMenus
            .filter(menu => {
              if (menu.title === "CPR Version" && !isCprEnabled) return false;
              if (menu.title === "Fringe Benefit Accounts" && !isFringeBenefitEnabled) return false;
              return true;
            })
            .map(menu => (
              <div key={formatKey(menu.title)}>
                {menu.subMenus && menu.subMenus.length > 0 ? (
                  <MuiSubMenuWrapper menuItemLabel={menu.title} subMenus={menu.subMenus} />
                ) : (
                  <div>
                    {menu.link ? (
                      <Link reloadDocument={menu.reloadDocument} to={`${menu.link}`} target={menu.openInNewTab ? "_blank" : ""}>
                        <MenuItem sx={{ width: "100%" }}>{menu.title}</MenuItem>
                      </Link>
                    ) : (
                      <MenuItem sx={{ width: "100%" }} disabled>
                        {menu.title}
                      </MenuItem>
                    )}
                    {menu.hasBreak && <hr />}
                  </div>
                )}
              </div>
            ))}
        </Menu>
      )}
    </>
  );
};

const SubHeaderBody = ({ tabs }) => {
  return (
    <>
      {tabs.map(tab => (
        <div className="tab_main" key={formatKey(tab.title)}>
          <MuiMenuWrapper
            dropdownTitle={tab.title}
            Icon={tab.icon}
            hasArrow={tab.hasArrow}
            href={tab.hasArrow ? "#" : tab.to}
            subMenus={tab.subMenus}
          />
        </div>
      ))}
    </>
  );
};

const SubHeaderV2 = ({ tabs }) => {
  const { defaultReportRoutes } = useSelector(routerSelector);
  const dashboardWithDropdown = {
    to: "/dashboard",
    title: "Dashboard",
    reference: R_DASHBOARD,
    hasArrow: true,
    icon: DashboardIcon,
    subMenus: [
      {
        link: "/reports/lost-report",
        title: "OTJ Hours and Progress",
        path: "lost-otj-hours-report",
        reference: R_PAYROLL_REPORTS,
        hasArrow: false,
        ignorePermission: true
      },
      {
        link: "/reports/lost-report",
        title: "OTJ Competency Report",
        path: "lost-otj-hours-report",
        reference: R_PAYROLL_REPORTS,
        hasArrow: false,
        ignorePermission: true
      },
      {
        link: "/reports/rti-report",
        title: "RTI Hours and Progress",
        path: "apprentice-progress-by-rap-report",
        reference: R_PAYROLL_REPORTS,
        hasArrow: false,
        ignorePermission: true
      },
      {
        link: "/reports/weekly-compliance-report",
        title: "Weekly Compliance Report",
        path: "weekly-compliance-report",
        reference: R_PAYROLL_REPORTS,
        hasArrow: false,
        ignorePermission: true
      }
    ]
  };
  dashboardWithDropdown.subMenus = dashboardWithDropdown.subMenus.filter(x => defaultReportRoutes.map(y => y.path).includes(x.path));
  const dashboardMenuDropdownEnabled = useFeatureFlagEnabled("dashboard-menu-dropdown-enabled");
  const editedTabs = tabs.slice();
  editedTabs.shift();
  editedTabs.unshift(dashboardWithDropdown);
  return <div className="subheader">{dashboardMenuDropdownEnabled ? <SubHeaderBody tabs={editedTabs} /> : <SubHeaderBody tabs={tabs} />}</div>;
};

export default SubHeaderV2;

SubHeaderV2.propTypes = {
  tabs: PropTypes.array.isRequired,
  onTabClick: PropTypes.func.isRequired
};
